import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import {
  Snackbar,
  SnackbarContent,
  Fade,
  IconButton,
  LinearProgress,
} from '@mui/material';

/**
 * MessageSnackBarKeyType
 */
export type MessageSnackBarKeyType = 'error' | 'info' | 'warning';

/**
 * MessageSnackBarType
 */
export type MessageSnackBarType = {
  /** キー */
  key: MessageSnackBarKeyType,
  /** コンテンツ */
  content: string,
};

/**
 * プロパティ
 */
export type Props = {
  /** メッセージ */
  message?: MessageSnackBarType,
  /** 隠す */
  hideSnackbar: () => void,
};

const MessageSnackbar: React.FC<Props> = ({ message, hideSnackbar }) => {
  const isOpen = Boolean(message);
  const key = message?.key;
  const content = message?.content ?? '';
  let background = '#ffffff';
  if (key === 'error') {
    background = '#d32f2f';
  } else if (key === 'info') {
    background = '#009900';
  } else if (key === 'warning') {
    background = '#c2a91b';
  }
  const autoHideMs = 5500;
  const [progress, setProgress] = React.useState(0);
  const isError = key === 'error';
  React.useEffect(() => {
    let mounted = true;
    let id: number | undefined;
    let timeoutId: number | undefined;
    if (isOpen && !isError) {
      const start = Date.now();
      id = window.setInterval(() => {
        if (!mounted) return;
        const elapsed = Date.now() - start;
        const v = Math.min(100, (elapsed / autoHideMs) * 100);
        setProgress(v);
      }, 80) as unknown as number;
      timeoutId = window.setTimeout(() => {
        hideSnackbar();
      }, autoHideMs + 500) as unknown as number;
    } else {
      setProgress(0);
    }
    return () => {
      mounted = false;
      if (id) window.clearInterval(id);
      if (timeoutId) window.clearTimeout(timeoutId);
      setProgress(0);
    };
  }, [isOpen, isError, content, key, hideSnackbar]);
  return (
    <Snackbar
      open={isOpen}
      // 自動クローズは独自タイマー管理。
      // info は clickaway で閉じる。他は無視。
      onClose={(_e, reason) => {
        if (!isError && message?.key === 'info' && reason === 'clickaway') {
          hideSnackbar();
        }
      }}
      TransitionComponent={Fade}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <SnackbarContent
        style={{ background, padding: 0, position: 'relative' }}
        message={(
          <div style={{ padding: '12px 16px 8px 16px' }}>
            <p style={{ margin: '0' }}>
              {key === 'error' ? <ErrorIcon /> : <InfoIcon />}
              <span
                style={{
                  fontSize: '1.2em',
                  verticalAlign: 'top',
                  paddingLeft: '5px',
                }}
              >
                {key}
              </span>
            </p>
            <p style={{ margin: '8px 0 8px 0' }}>{content}</p>
            {!isError && isOpen && (
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 0,
                  transform: 'translateX(-50%)',
                  width: 320,
                  padding: '0 16px 8px 16px',
                  boxSizing: 'border-box',
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={100 - progress}
                  sx={{
                    height: 3,
                    background: 'rgba(255,255,255,0.35)',
                    '& .MuiLinearProgress-bar': { backgroundColor: 'rgba(255,255,255,0.9)' },
                  }}
                />
              </div>
            )}
          </div>
        )}
        action={(
          <IconButton
            aria-label="閉じる"
            onClick={hideSnackbar}
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: 'rgba(0,0,0,0.7)',
              boxShadow: 1,
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </Snackbar>
  );
};

export default MessageSnackbar;
